import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from './AuthContext'

/* ── Types for each backend slice ──────────────────────────────────── */

export interface HeatmapData {
  attractions: { name: string; type: string; hours: Record<number, number> }[]
  hours: number[]
  max_scans: number
}

export interface VelocityPoint {
  hour: number
  revenue: number
  orders: number
  cumulative: number
}

export interface VelocityData {
  points: VelocityPoint[]
  total: number
  peak_hour: number
  peak_revenue: number
}

export interface PlanScatterPoint {
  plan_name: string
  plan_type: string
  price: number
  units_sold: number
  revenue: number
  avg_scans: number
  avg_attractions: number
  avg_duration: number
}

export interface InsightEvidence {
  source: string
  metric: string
  value: number
  label: string
}

export interface InsightCard {
  type: 'highlight' | 'warning' | 'info' | 'metric'
  icon: string
  title: string
  body: string
  evidence?: InsightEvidence[]
  method?: string
}

export interface AttractionDeep {
  attraction_id: number
  name: string
  type: string
  total_scans: number
  peak_scans: number
  peak_hour: number
  avg_scans_per_hour: number
  active_hours: number
  capacity_utilization: { hour: number; pct: number }[]
  cycle_time_sec: number | null
  est_wait_avg_sec: number | null
  est_wait_worst_sec: number | null
  batch_size: number | null
  cycles_per_hour: number | null
  peak_15min_scans: number | null
  throughput_per_min: number | null
  staff_count: number
}

export interface AgentDeep {
  agent_id: number
  name: string
  attractions: string[]
  total_scans: number
  revenue: number
  bands_served: number
  shift_start: number
  shift_end: number
  shift_hours: number
  active_hours: number
  idle_hours: number
  scans_per_hour: number
  utilization_pct: number
  hourly: { hour: number; scans: number; revenue: number }[]
}

export interface TransitNode {
  id: number
  name: string
  x: number
  y: number
  total_scans: number
  type: string
  revenue: number
}

export interface TransitEdge {
  from: number
  to: number
  from_name: string
  to_name: string
  observations: number
  median_sec: number
  walk_sec: number
  distance_m: number
}

export interface ProximityRevenueRow {
  name: string
  type: string
  revenue: number
  avg_dist_to_top_rides_m: number
}

export interface TransitGraphData {
  nodes: TransitNode[]
  edges: TransitEdge[]
  proximity_revenue: ProximityRevenueRow[]
  top_rides_for_proximity: { id: number; name: string }[]
  node_count: number
  edge_count: number
}

export interface StaffSparkline {
  agent_id: number
  name: string
  attractions: string[]
  total_scans: number
  revenue: number
  hourly: { hour: number; scans: number }[]
}

export interface IntelligencePayload {
  source: string
  event_id: number
  heatmap: HeatmapData
  velocity: VelocityData
  plan_scatter: PlanScatterPoint[]
  staff_sparklines: StaffSparkline[]
  insights: InsightCard[]
  attraction_deep: AttractionDeep[]
  agent_deep: AgentDeep[]
  transit_graph: TransitGraphData
  generated_at: string
}

export interface DashboardStats {
  source: string
  event: {
    event_id: number
    event_name: string
    event_date: string
    status: string
  }
  metrics: {
    revenue_total: number
    ride_gross: number
    guests: number
    riders_per_hour: number
    orders_per_hour: number
  }
  hourly: { hour: number; orders: number; revenue: number; riders: number }[]
  generated_at: string
}

export interface DashboardExtras {
  source: string
  event_id: number
  plan_mix: { rank: number; plan_name: string; orders: number; revenue: number; pct_of_orders: number }[]
  kiosk_throughput: { rank: number; kiosk_group_name: string; placement_type: string | null; orders: number; revenue: number }[]
  top_attractions: { rank: number; attraction_id: number; attraction_name: string; attraction_type: string; scans: number; unique_guests: number; revenue: number; repeat_play_pct: number }[]
  daily_breakdown?: { day: string; scans: number; guests: number; revenue: number; paid_scans: number }[]
  generated_at: string
}

export interface EntityDetails {
  source: string
  event_id: number
  plans: { rank: number; plan_id: number; plan_name: string; plan_type: string; price: number; units_sold: number; revenue: number }[]
  attractions: { rank: number; attraction_id: number; attraction_name: string; attraction_type: string; total_scans: number; paid_scans: number; unique_guests: number; revenue: number; repeat_play_pct: number; peak_hour_scans: number; avg_scans_per_hour: number; active_hours: number; performance: string; z_score: number }[]
  employees: {
    humans: { employee_id: number; name: string; role: string; assigned_attraction: string; total_scans: number; revenue: number; unique_bands: number; active_hours: number; scans_per_hour: number }[]
    kiosks: { employee_id: number; name: string; role: string; assigned_attraction: string; total_scans: number; revenue: number; unique_bands: number; active_hours: number; scans_per_hour: number }[]
    ride_scanners: { employee_id: number; name: string; role: string; assigned_attraction: string; total_scans: number; revenue: number; unique_bands: number; active_hours: number; scans_per_hour: number }[]
  }
  kiosks: { rank: number; kiosk_id: number; kiosk_name: string; kiosk_type: string; kiosk_group: string; orders: number; revenue: number; orders_per_hour: number; avg_transaction_value: number }[]
  generated_at: string
}

export interface RevenueBreakdownItem {
  type: string
  label: string
  cents: number
  orders?: number
  transactions?: number
  unique_bands?: number
}

export interface RevenueBreakdown {
  source: string
  event_id: number
  revenue: {
    total_gross_cents: number
    settlement_gross_cents: number | null
    settlement_fees_cents: number | null
    settlement_net_cents: number | null
  }
  purchase_breakdown: RevenueBreakdownItem[]
  payment_method: RevenueBreakdownItem[]
  credit_allocation: RevenueBreakdownItem[]
  breakage: { total_cents: number; bands_with_breakage: number }
  credit_utilization: { total_loaded_cents: number; total_spent_cents: number; utilization_rate: number }
  total_orders: number
  generated_at: string
}

export interface YoyPctMap {
  revenue_total?: number
  ride_gross?: number
  guests?: number
  riders_per_hour?: number
  orders_per_hour?: number
}

export interface EventDay {
  date: string
  revenue: number
  scans: number
}

export interface EventDaysPayload {
  event_id: number
  event_name: string
  start_date: string
  end_date: string
  status: string
  today: string
  days: EventDay[]
}

export interface YoyDayPayload {
  current: { date: string; event_id: number; event_name: string; metrics: DashboardStats['metrics'] } | null
  prior_year: { date: string; event_id: number; event_name: string; metrics: DashboardStats['metrics'] } | null
  deltas: YoyPctMap | null
  reason?: string
}

/* ── Context shape ─────────────────────────────────────────────────── */

export interface EventDataState {
  intelligence: IntelligencePayload | null
  stats: DashboardStats | null
  extras: DashboardExtras | null
  entities: EntityDetails | null
  revenueBreakdown: RevenueBreakdown | null
  yoy: YoyPctMap | null
  selectedDate: string | null
  setSelectedDate: (date: string | null) => void
  eventDays: EventDaysPayload | null
  yoyDay: YoyDayPayload | null
  dayStats: DashboardStats | null
  dayLoading: boolean
  loading: boolean
  error: string | null
  warnings: string[]
  refresh: () => void
}

const EventDataContext = createContext<EventDataState>({
  intelligence: null,
  stats: null,
  extras: null,
  entities: null,
  revenueBreakdown: null,
  yoy: null,
  selectedDate: null,
  setSelectedDate: () => {},
  eventDays: null,
  yoyDay: null,
  dayStats: null,
  dayLoading: false,
  loading: false,
  error: null,
  warnings: [],
  refresh: () => {},
})

export function useEventData() {
  return useContext(EventDataContext)
}

/* ── Provider ──────────────────────────────────────────────────────── */

async function fetchJson<T>(url: string, signal: AbortSignal): Promise<T | null> {
  try {
    const res = await fetch(url, { credentials: 'same-origin', signal })
    if (!res.ok) return null
    return (await res.json()) as T
  } catch {
    return null
  }
}

export function EventDataProvider({ children }: { children: ReactNode }) {
  const { currentEventId } = useAuth()
  const [intelligence, setIntelligence] = useState<IntelligencePayload | null>(null)
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [extras, setExtras] = useState<DashboardExtras | null>(null)
  const [entities, setEntities] = useState<EntityDetails | null>(null)
  const [revenueBreakdown, setRevenueBreakdown] = useState<RevenueBreakdown | null>(null)
  const [yoy, setYoy] = useState<YoyPctMap | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [eventDays, setEventDays] = useState<EventDaysPayload | null>(null)
  const [yoyDay, setYoyDay] = useState<YoyDayPayload | null>(null)
  const [dayStats, setDayStats] = useState<DashboardStats | null>(null)
  const [dayLoading, setDayLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const abortRef = useRef<AbortController | null>(null)
  const dayAbortRef = useRef<AbortController | null>(null)
  const generationRef = useRef(0)
  const lastEventRef = useRef<number | null>(null)

  const fetchAll = useCallback((eventId: number) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const generation = ++generationRef.current
    setLoading(true)
    setError(null)
    setWarnings([])

    const base = `/api/dashboard/${eventId}`
    Promise.all([
      fetchJson<IntelligencePayload>(`${base}/intelligence`, controller.signal),
      fetchJson<DashboardStats>(`${base}/stats`, controller.signal),
      fetchJson<DashboardExtras>(`${base}/extras`, controller.signal),
      fetchJson<EntityDetails>(`${base}/entities`, controller.signal),
      fetchJson<{ yoy_pct?: YoyPctMap }>(`${base}/compare`, controller.signal),
      fetchJson<RevenueBreakdown>(`${base}/revenue-breakdown`, controller.signal),
      fetchJson<EventDaysPayload>(`${base}/days`, controller.signal),
    ]).then(([intel, st, ext, ent, cmp, rev, days]) => {
      if (generationRef.current !== generation) return
      setIntelligence(intel)
      setStats(st)
      setExtras(ext)
      setEntities(ent)
      setRevenueBreakdown(rev)
      setYoy(cmp?.yoy_pct ?? null)
      setEventDays(days)
      setLoading(false)
      if (!intel && !st && !ext && !ent) {
        setError('No data available for this event')
      } else {
        const w: string[] = []
        if (st && !ext) w.push('Kiosk/plan breakdown unavailable')
        if (st && !ent) w.push('Entity details unavailable')
        if (st && !rev) w.push('Revenue breakdown unavailable')
        setWarnings(w)
      }
    }).catch(() => {
      if (controller.signal.aborted) return
      setError('Failed to load event data')
      setLoading(false)
    })
  }, [])

  const fetchDay = useCallback((eventId: number, date: string) => {
    dayAbortRef.current?.abort()
    const controller = new AbortController()
    dayAbortRef.current = controller
    setDayLoading(true)

    const base = `/api/dashboard/${eventId}`
    Promise.all([
      fetchJson<DashboardStats>(`${base}/stats?date=${date}`, controller.signal),
      fetchJson<YoyDayPayload>(`${base}/yoy?date=${date}`, controller.signal),
    ]).then(([st, yoyRes]) => {
      if (controller.signal.aborted) return
      setDayStats(st)
      setYoyDay(yoyRes)
      setDayLoading(false)
    }).catch(() => {
      if (controller.signal.aborted) return
      setDayLoading(false)
    })
  }, [])

  useEffect(() => {
    if (currentEventId == null) {
      setIntelligence(null)
      setStats(null)
      setExtras(null)
      setEntities(null)
      setRevenueBreakdown(null)
      setYoy(null)
      setEventDays(null)
      setSelectedDate(null)
      setYoyDay(null)
      setDayStats(null)
      setLoading(false)
      setDayLoading(false)
      setError(null)
      lastEventRef.current = null
      return
    }
    if (currentEventId === lastEventRef.current) return
    lastEventRef.current = currentEventId
    setSelectedDate(null)
    setYoyDay(null)
    setDayStats(null)
    fetchAll(currentEventId)
    return () => { abortRef.current?.abort(); dayAbortRef.current?.abort() }
  }, [currentEventId, fetchAll])

  useEffect(() => {
    if (currentEventId == null || selectedDate == null) {
      setDayStats(null)
      setYoyDay(null)
      return
    }
    fetchDay(currentEventId, selectedDate)
    return () => { dayAbortRef.current?.abort() }
  }, [currentEventId, selectedDate, fetchDay])

  const refresh = useCallback(() => {
    if (currentEventId != null) {
      lastEventRef.current = null
      fetchAll(currentEventId)
      if (selectedDate) fetchDay(currentEventId, selectedDate)
    }
  }, [currentEventId, selectedDate, fetchAll, fetchDay])

  const value = useMemo<EventDataState>(() => ({
    intelligence, stats, extras, entities, revenueBreakdown, yoy,
    selectedDate, setSelectedDate, eventDays, yoyDay, dayStats, dayLoading,
    loading, error, warnings, refresh,
  }), [intelligence, stats, extras, entities, revenueBreakdown, yoy,
    selectedDate, eventDays, yoyDay, dayStats, dayLoading,
    loading, error, warnings, refresh])

  return (
    <EventDataContext.Provider value={value}>
      {children}
    </EventDataContext.Provider>
  )
}
